import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';

/**
 * Editorial legal-page shell. Uses explicit Tailwind classes for body content
 * because the tailwind typography (`prose`) plugin is not installed in patronez.
 *
 * Children should compose using the helpers from `legal-shell.tsx` or the
 * `LegalH2 / LegalP / LegalUL / LegalLi` exports below.
 */
export function LegalShell({ titleKey, children }: { titleKey: string; children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-6 py-12 md:py-16">
        <p className="text-xs uppercase tracking-wide text-accent-600 font-medium mb-2">{t('brand.name')}</p>
        <h1 className="display text-4xl font-semibold tracking-tight text-ink-900 serif-jp">{t(titleKey)}</h1>
        <div className="mt-6 text-ink-700 text-sm leading-relaxed">{children}</div>

        <LegalEntityBlock />

        <Link to="/" className="inline-flex items-center mt-10 text-sm font-semibold text-accent-700 hover:text-accent-600">
          ← {t('common.back_home')}
        </Link>
      </main>
      <SiteFooter />
    </div>
  );
}

/* ── editorial primitives ───────────────────────────────────────── */

export function LegalLead({ children }: { children: React.ReactNode }) {
  return <p className="text-[15px] text-ink-700 leading-relaxed">{children}</p>;
}

export function LegalH2({ children }: { children: React.ReactNode }) {
  return <h2 className="mt-8 text-lg font-semibold text-ink-900">{children}</h2>;
}

export function LegalP({ children }: { children: React.ReactNode }) {
  return <p className="mt-3 text-[14px] text-ink-700 leading-relaxed">{children}</p>;
}

export function LegalUL({ children }: { children: React.ReactNode }) {
  return <ul className="list-disc pl-5 mt-2 space-y-1 text-[14px] text-ink-700 leading-relaxed">{children}</ul>;
}

export function LegalLastUpdated({ children }: { children: React.ReactNode }) {
  return <p className="mt-10 pt-6 border-t border-ink-200 text-xs uppercase tracking-wide text-ink-500">{children}</p>;
}

/**
 * Shared operating-entity + counsel-review block shown at the foot of every
 * legal page. The entity name/address are owner-fill placeholders until the
 * registered operating company is provisioned (see the 特定商取引法 page).
 */
export function LegalEntityBlock() {
  const { t } = useTranslation();
  return (
    <div className="mt-10 pt-6 border-t border-ink-200">
      <h2 className="text-sm font-semibold text-ink-900">{t('legal.entity.title')}</h2>
      <p className="mt-2 text-[13px] text-ink-600 leading-relaxed">{t('legal.entity.body')}</p>
      <dl className="mt-3 grid grid-cols-1 sm:grid-cols-[8rem_1fr] gap-x-4 gap-y-1.5 text-[13px]">
        {[
          ['legal.entity.name_label', 'legal.entity.name_value'],
          ['legal.entity.address_label', 'legal.entity.address_value'],
          ['legal.entity.contact_label', 'legal.entity.contact_value'],
        ].map(([l, v]) => (
          <div key={l} className="sm:contents">
            <dt className="text-ink-500">{t(l)}</dt>
            <dd className="text-ink-800">{t(v)}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-4 text-[12px] text-ink-400 italic">{t('legal.entity.review_note')}</p>
      <Link to="/tokusho" className="mt-3 inline-block text-[13px] font-medium text-accent-700 hover:text-accent-600">
        {t('footer.legal_tokusho')} →
      </Link>
    </div>
  );
}

/* ── Security ───────────────────────────────────────────────────── */

export function SecurityPage() {
  const { t } = useTranslation();
  return (
    <LegalShell titleKey="footer.security">
      <LegalLead>{t('legal.security.lead')}</LegalLead>

      <LegalH2>{t('legal.security.encryption_title')}</LegalH2>
      <LegalP>{t('legal.security.encryption_body')}</LegalP>

      <LegalH2>{t('legal.security.auth_title')}</LegalH2>
      <LegalP>{t('legal.security.auth_body')}</LegalP>

      <LegalH2>{t('legal.security.isolation_title')}</LegalH2>
      <LegalP>{t('legal.security.isolation_body')}</LegalP>

      <LegalH2>{t('legal.security.audit_title')}</LegalH2>
      <LegalP>{t('legal.security.audit_body')}</LegalP>

      <LegalH2>{t('legal.security.hosting_title')}</LegalH2>
      <LegalP>{t('legal.security.hosting_body')}</LegalP>

      <LegalH2>{t('legal.security.backup_title')}</LegalH2>
      <LegalP>{t('legal.security.backup_body')}</LegalP>

      <LegalH2>{t('legal.security.vendor_title')}</LegalH2>
      <LegalP>
        {t('legal.security.vendor_body_prefix')}{' '}
        <Link to="/subprocessors" className="text-accent-700 hover:text-accent-600 underline underline-offset-2">
          patronez.com/subprocessors
        </Link>{' '}
        {t('legal.security.vendor_body_suffix')}
      </LegalP>

      <LegalH2>{t('legal.security.pentest_title')}</LegalH2>
      <LegalP>{t('legal.security.pentest_body')}</LegalP>

      <LegalH2>{t('legal.security.report_title')}</LegalH2>
      <LegalP>
        {t('legal.security.report_body_prefix')} <code className="text-ink-900 font-mono">security@patronez.com</code>.{' '}
        {t('legal.security.report_body_suffix')}
      </LegalP>
    </LegalShell>
  );
}

/* ── Trust — how PatronEZ's AI works (transparency) ─────────────── */

export function TrustPage() {
  const { t } = useTranslation();
  return (
    <LegalShell titleKey="footer.trust">
      <LegalLead>{t('legal.trust.lead')}</LegalLead>

      <LegalH2>{t('legal.trust.models_title')}</LegalH2>
      <LegalP>{t('legal.trust.models_body')}</LegalP>

      <LegalH2>{t('legal.trust.cost_title')}</LegalH2>
      <LegalP>{t('legal.trust.cost_body')}</LegalP>

      <LegalH2>{t('legal.trust.citations_title')}</LegalH2>
      <LegalP>{t('legal.trust.citations_body')}</LegalP>

      <LegalH2>{t('legal.trust.human_title')}</LegalH2>
      <LegalP>{t('legal.trust.human_body')}</LegalP>

      <LegalH2>{t('legal.trust.training_title')}</LegalH2>
      <LegalP>{t('legal.trust.training_body')}</LegalP>

      <LegalH2>{t('legal.trust.keigo_title')}</LegalH2>
      <LegalP>{t('legal.trust.keigo_body')}</LegalP>

      <LegalH2>{t('legal.trust.registry_title')}</LegalH2>
      <LegalP>{t('legal.trust.registry_body')}</LegalP>
    </LegalShell>
  );
}

/* ── Status (kept as-is per orchestrator note) ──────────────────── */

export function StatusPage() {
  const { t } = useTranslation();
  return (
    <LegalShell titleKey="footer.status">
      <LegalLead>{t('legal.status.lead')}</LegalLead>
      <ul className="mt-6 space-y-2 text-[14px] text-ink-700">
        <li className="flex items-center gap-3"><span className="inline-block w-2 h-2 rounded-full bg-emerald-500" /> API · {t('legal.status.operational')}</li>
        <li className="flex items-center gap-3"><span className="inline-block w-2 h-2 rounded-full bg-emerald-500" /> Web · {t('legal.status.operational')}</li>
        <li className="flex items-center gap-3"><span className="inline-block w-2 h-2 rounded-full bg-emerald-500" /> AI agents · {t('legal.status.operational')}</li>
        <li className="flex items-center gap-3"><span className="inline-block w-2 h-2 rounded-full bg-emerald-500" /> Signal ingestion · {t('legal.status.operational')}</li>
      </ul>
    </LegalShell>
  );
}

/* ── Changelog (kept as-is per orchestrator note) ───────────────── */

export function ChangelogPage() {
  const { t } = useTranslation();
  return (
    <LegalShell titleKey="footer.changelog">
      <h2 className="text-base font-semibold text-ink-900">{t('legal.changelog.v11_date')}</h2>
      <ul className="list-disc pl-5 mt-2 space-y-1 text-[14px] text-ink-700">
        <li>{t('legal.changelog.v11_send')}</li>
        <li>{t('legal.changelog.v11_edinet')}</li>
        <li>{t('legal.changelog.v11_inbox')}</li>
      </ul>

      <h2 className="mt-8 text-base font-semibold text-ink-900">2026-05-23 — v1</h2>
      <ul className="list-disc pl-5 mt-2 space-y-1 text-[14px] text-ink-700">
        <li>{t('legal.changelog.v1_initial')}</li>
        <li>{t('legal.changelog.v1_meishi')}</li>
        <li>{t('legal.changelog.v1_signals')}</li>
        <li>{t('legal.changelog.v1_keigo')}</li>
        <li>{t('legal.changelog.v1_agents')}</li>
        <li>{t('legal.changelog.v1_oauth')}</li>
        <li>{t('legal.changelog.v1_languages')}</li>
      </ul>
    </LegalShell>
  );
}

/* ── Terms of Service ───────────────────────────────────────────── */

export function TermsPage() {
  const { t } = useTranslation();
  return (
    <LegalShell titleKey="footer.terms">
      <LegalLead>{t('legal.terms.lead')}</LegalLead>

      <LegalH2>{t('legal.terms.service_title')}</LegalH2>
      <LegalP>{t('legal.terms.service_body')}</LegalP>

      <LegalH2>{t('legal.terms.account_title')}</LegalH2>
      <LegalP>{t('legal.terms.account_body')}</LegalP>

      <LegalH2>{t('legal.terms.billing_title')}</LegalH2>
      <LegalP>{t('legal.terms.billing_body')}</LegalP>

      <LegalH2>{t('legal.terms.aup_title')}</LegalH2>
      <LegalP>
        {t('legal.terms.aup_body_prefix')}{' '}
        <Link to="/acceptable-use" className="text-accent-700 hover:text-accent-600 underline underline-offset-2">
          {t('legal.terms.aup_body_link')}
        </Link>
        {t('legal.terms.aup_body_suffix')}
      </LegalP>

      <LegalH2>{t('legal.terms.data_title')}</LegalH2>
      <LegalP>{t('legal.terms.data_body')}</LegalP>

      <LegalH2>{t('legal.terms.confidentiality_title')}</LegalH2>
      <LegalP>{t('legal.terms.confidentiality_body')}</LegalP>

      <LegalH2>{t('legal.terms.sla_title')}</LegalH2>
      <LegalP>{t('legal.terms.sla_body')}</LegalP>

      <LegalH2>{t('legal.terms.warranties_title')}</LegalH2>
      <LegalP>{t('legal.terms.warranties_body')}</LegalP>

      <LegalH2>{t('legal.terms.liability_title')}</LegalH2>
      <LegalP>{t('legal.terms.liability_body')}</LegalP>

      <LegalH2>{t('legal.terms.indemnity_title')}</LegalH2>
      <LegalP>{t('legal.terms.indemnity_body')}</LegalP>

      <LegalH2>{t('legal.terms.term_title')}</LegalH2>
      <LegalP>{t('legal.terms.term_body')}</LegalP>

      <LegalH2>{t('legal.terms.law_title')}</LegalH2>
      <LegalP>{t('legal.terms.law_body')}</LegalP>

      <LegalH2>{t('legal.terms.changes_title')}</LegalH2>
      <LegalP>{t('legal.terms.changes_body')}</LegalP>

      <LegalLastUpdated>{t('legal.terms.last_updated')}</LegalLastUpdated>
    </LegalShell>
  );
}

/* ── Privacy Policy ─────────────────────────────────────────────── */

export function PrivacyPage() {
  const { t } = useTranslation();
  return (
    <LegalShell titleKey="footer.privacy">
      <LegalLead>{t('legal.privacy.lead')}</LegalLead>

      <LegalH2>{t('legal.privacy.collect_title')}</LegalH2>
      <LegalP>{t('legal.privacy.collect_body')}</LegalP>

      <LegalH2>{t('legal.privacy.why_title')}</LegalH2>
      <LegalP>{t('legal.privacy.why_body')}</LegalP>

      <LegalH2>{t('legal.privacy.basis_title')}</LegalH2>
      <LegalP>{t('legal.privacy.basis_body')}</LegalP>

      <LegalH2>{t('legal.privacy.transfers_title')}</LegalH2>
      <LegalP>{t('legal.privacy.transfers_body')}</LegalP>

      <LegalH2>{t('legal.privacy.retention_title')}</LegalH2>
      <LegalP>{t('legal.privacy.retention_body')}</LegalP>

      <LegalH2>{t('legal.privacy.gdpr_rights_title')}</LegalH2>
      <LegalP>{t('legal.privacy.gdpr_rights_body')}</LegalP>

      <LegalH2>{t('legal.privacy.appi_rights_title')}</LegalH2>
      <LegalP>{t('legal.privacy.appi_rights_body')}</LegalP>

      <LegalH2>{t('legal.privacy.ccpa_rights_title')}</LegalH2>
      <LegalP>{t('legal.privacy.ccpa_rights_body')}</LegalP>

      <LegalH2>{t('legal.privacy.children_title')}</LegalH2>
      <LegalP>{t('legal.privacy.children_body')}</LegalP>

      <LegalH2>{t('legal.privacy.cookies_title')}</LegalH2>
      <LegalP>
        {t('legal.privacy.cookies_body_prefix')}{' '}
        <Link to="/cookies" className="text-accent-700 hover:text-accent-600 underline underline-offset-2">
          {t('legal.privacy.cookies_body_link')}
        </Link>{' '}
        {t('legal.privacy.cookies_body_suffix')}
      </LegalP>

      <LegalH2>{t('legal.privacy.subprocessors_title')}</LegalH2>
      <LegalP>
        {t('legal.privacy.subprocessors_body_prefix')}{' '}
        <Link to="/subprocessors" className="text-accent-700 hover:text-accent-600 underline underline-offset-2">
          patronez.com/subprocessors
        </Link>{' '}
        {t('legal.privacy.subprocessors_body_suffix')}
      </LegalP>

      <LegalH2>{t('legal.privacy.dpa_title')}</LegalH2>
      <LegalP>
        {t('legal.privacy.dpa_body_prefix')}{' '}
        <Link to="/dpa" className="text-accent-700 hover:text-accent-600 underline underline-offset-2">
          {t('legal.privacy.dpa_body_link')}
        </Link>
        {t('legal.privacy.dpa_body_suffix')}
      </LegalP>

      <LegalH2>{t('legal.privacy.contact_title')}</LegalH2>
      <LegalP>
        {t('legal.privacy.contact_body_prefix')}{' '}
        <code className="text-ink-900 font-mono">privacy@patronez.com</code>.{' '}
        {t('legal.privacy.contact_body_suffix')}
      </LegalP>

      <LegalH2>{t('legal.privacy.changes_title')}</LegalH2>
      <LegalP>{t('legal.privacy.changes_body')}</LegalP>

      <LegalLastUpdated>{t('legal.privacy.last_updated')}</LegalLastUpdated>
    </LegalShell>
  );
}
